import { createContext, use, useEffect, type ReactElement, type ReactNode } from 'react'
import { Outlet, useNavigate, useRouterContext } from './flashcards-fe.router-bindings'
import { WorkspaceClosedError, type SpacesResources } from './spaces-resources'
import { useSession } from '@w/auth-fe'
import { useMutation, useQuery } from '@w/react-query'
import { Button } from '@w/ui/components/button'
import { ErrorBoundary, ErrorFallback } from '@w/ui/system/error-boundary'
import { unknownErrorToError } from '@w/utils'

/** A read issued after its workspace was dropped (rejoin) renders as unavailable; the router re-resolves on retry. */
function RouteErrorFallback({ error, resetErrorBoundary }: { error: unknown; resetErrorBoundary?: () => void }) {
  if (!(error instanceof WorkspaceClosedError)) return <ErrorFallback error={error} />
  return <main className="p-6"><p>Page unavailable</p><button type="button" onClick={resetErrorBoundary}>Retry</button></main>
}

const UserSpacesContext = createContext<SpacesResources | null>(null)

/**
 * Boundary of every protected route. It renders the session snapshot it
 * holds (@w/auth-fe session owner): after sign-out it drops its subtree, and
 * the session closes once that unmount committed. Renders in between still read
 * open resources.
 */
// Explicit return type: the route tree's type includes this component, and an
// inferred type through Outlet would be circular.
export function AuthenticatedLayout(): ReactElement | null {
  const ctx = useRouterContext()
  const spaces = useSession(ctx.session)
  useEffect(() => (spaces ? ctx.session.hold(spaces) : undefined), [ctx, spaces])
  const navigate = useNavigate()
  // A sign-out no view asked for (an rpc answered 401, `signOutOnUnauthorized`) drops this subtree: land on login.
  // The anonymous principal is no login yet stays a principal (flashcards-local-first.spec.md), so only "no principal" leaves.
  useEffect(() => { if (!spaces && !ctx.auth.getUserOrNull()) navigate({ to: '/login' }) }, [ctx, spaces, navigate])
  if (!spaces) return null
  return (
    // Keyed by user: a direct replacement remounts views that captured the previous user.
    <UserSpacesContext key={spaces.userId} value={spaces}>
      <ErrorBoundary fallback={RouteErrorFallback}><ResetImportGate><Outlet /></ResetImportGate></ErrorBoundary>
    </UserSpacesContext>
  )
}

/**
 * .agents/skills/em/requirements.md 2026-10-27 16:5x: after a production reset, a device holding data from before
 * it blocks the app on "Import your data"; nothing is uploaded until the user chooses to.
 */
function ResetImportGate({ children }: { children: ReactNode }) {
  const ctx = useRouterContext()
  const spaces = useUserSpaces()
  const held = useQuery(spaces.resetImportOptions(), ctx.queryClient)
  const importData = useMutation({ mutationFn: async (workspaceIds: string[]) => {
    // Replacement document data is unknown until import reads and publishes the replacement replica.
    for (const id of workspaceIds) await spaces.importResetData(id, ctx.tel)
  } })
  const workspaceIds = held.data ?? []
  if (workspaceIds.length === 0) return children
  return <main aria-labelledby="reset-import-title" className="mx-auto flex max-w-md flex-col gap-6 p-6">
    <h1 id="reset-import-title" className="text-xl font-semibold">Your data from before the reset</h1>
    <p className="text-muted-foreground">The server was reset. This device still holds your pages from before the reset. Nothing has been uploaded. Import them into your workspace to continue.</p>
    {importData.error ? <p role="alert">Import failed: {unknownErrorToError(importData.error).message}</p> : null}
    <Button data-testid="reset-import" loading={importData.isPending} onClick={() => { importData.mutate(workspaceIds) }}>Import your data</Button>
  </main>
}

/** The signed-in user's resources, for rendering inside a protected route. */
export function useUserSpaces(): SpacesResources {
  const spaces = use(UserSpacesContext)
  if (!spaces) throw new Error('useUserSpaces must render inside AuthenticatedLayout')
  return spaces
}
